export default function measurementConverter() {
    // Match 2-part or 3-part dimension strings like:
    // 10'' x 12'' or 10' x 12'' x 8''
    const dimensionPattern = /(\d+(?:\.\d+)?\s*(?:''|"|'))\s*x\s*(\d+(?:\.\d+)?\s*(?:''|"|'))(?:\s*x\s*(\d+(?:\.\d+)?\s*(?:''|"|')))?/gi;

    return {
        unitMode: 'imperial',
        originalTextNodes: new WeakMap(),
        textNodes: [],
        observer: null,
        refreshTimer: null,
        init() {
            this.syncUnitMode();
            this.refresh();
            this.startObserver();
            this.$watch('$store.measurementUnit.mode', () => {
                this.syncUnitMode();
                this.applyMode();
            });
        },
        syncUnitMode() {
            this.unitMode = this.$store.measurementUnit.mode;
        },
        refresh() {
            // Rebuild the cache from the current DOM. This is needed for
            // dynamic regions where Alpine inserts new nodes via x-for.
            this.textNodes = [];
            this.originalTextNodes = new WeakMap();
            this.cacheTextNodes();
            this.applyMode();
        },
        startObserver() {
            // Watch for child nodes added or removed inside this converter's
            // subtree.
            this.observer = new MutationObserver(() => {
                this.refresh();
            });

            this.observer.observe(this.$el, {
                childList: true,
                subtree: true,
            });
        },
        cacheTextNodes() {
            // Cache the original text once so metric mode can be fully reversed.
            // This keeps the source content in imperial and lets us re-apply
            // conversion from a clean original string every time.
            const walker = document.createTreeWalker(
                this.$el,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: (node) => {
                        if (!node.nodeValue.trim()) {
                            return NodeFilter.FILTER_REJECT;
                        }

                        return NodeFilter.FILTER_ACCEPT;
                    },
                }
            );

            let node = walker.nextNode();

            while (node) {
                this.textNodes.push(node);
                this.originalTextNodes.set(node, node.nodeValue);
                node = walker.nextNode();
            }
        },
        applyMode() {
            // Swap each cached text node between its original imperial value
            // and a converted metric version based on the current store mode.
            this.textNodes.forEach((node) => {
                const originalText = this.originalTextNodes.get(node);

                if (!originalText) {
                    return;
                }

                if (this.unitMode === 'metric') {
                    node.nodeValue = this.convertTextToMetric(originalText);
                    return;
                }

                node.nodeValue = originalText;
            });
        },
        convertTextToMetric(text) {
            // Replace every supported imperial dimension string found in a
            // text node, leaving the rest of the sentence untouched.
            return text.replace(dimensionPattern, (match) => {
                return this.convertDimensionMatch(match);
            });
        },
        convertDimensionMatch(match) {
            // Break a full dimension string like "10'' x 12'' x 8''" into
            // individual segments, convert each one, then join them back.
            const segments = match
                .split(/\s*x\s*/i)
                .map((segment) => this.convertSegmentToMetric(segment));

            return `${segments.join(' x ')} cm`;
        },
        convertSegmentToMetric(segment) {
            // Capture a numeric value followed by an imperial token:
            // '' or " for inches, ' for feet.
            const parsedSegment = segment.trim().match(/^(\d+(?:\.\d+)?)\s*(''|"|')$/);

            if (!parsedSegment) {
                return segment;
            }

            const value = Number(parsedSegment[1]);
            const unit = parsedSegment[2];

            if (Number.isNaN(value)) {
                return segment;
            }

            if (unit === "'") {
                // Feet to centimeters: 1 ft = 30.48 cm
                return this.formatMetricValue(value * 30.48);
            }

            // Inches to centimeters: 1 in = 2.54 cm
            return this.formatMetricValue(value * 2.54);
        },
        formatMetricValue(valueInCentimeters) {
            return Number(valueInCentimeters.toFixed(1)).toString();
        },
    };
}
